<template>
    <div>
        <label v-if="label" class="label">{{ label }}</label>
        <e-autocomplete
            ref="autocomplete"
            v-model="selected"
            clearable
            :data="displayData"
            :set-value="setValue"
            :set-selected="setValue"
            keep-first
            :delay="0"
            :label="label"
            field="name"
            :initial-data="initialData"
            open-on-focus
            :placeholder="placeholder"
            url-params="groups"
            :icon="includeIcon ? 'filter' : ''"
            append-to-body
            @typing="handleTyping"
            @focus="loadInitialData"
            @esc="$emit('update:modelValue', null)"
            @select="group => $emit('update:modelValue', group)"
            @clear="handleClear"
        >
            <template #default="props">
                {{ props.option.name }}
            </template>
        </e-autocomplete>
        <div v-if="error" class="error-message">{{ error }}</div>
    </div>
</template>

<script>
import debounce from 'lodash/debounce.js';

import axios from '@/plugins/axios.js';

export default {
    name: 'GroupSearch',

    props: {
        label: {
            type: String,
            required: false,
            default: '',
            value: '',
        },

        placeholder: {
            type: String,
            required: false,
            default: 'Type a group title...',
        },

        modelValue: {
            type: Object,
            required: false,
            default: () => ({}),
        },

        initialData: {
            type: Array,
            required: false,
            default: () => [],
        },

        setValue: {
            type: String,
            required: false,
            default: null,
        },

        presetId: {
            type: Number,
            required: false,
            default: null,
        },

        includeIcon: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data() {
        return {
            data: [],
            selected: null,
            isFetching: false,
            error: null,
            latestSearchTerm: '',
            requestId: 0,
        };
    },

    computed: {
        displayData() {
            const searchTerm = (this.latestSearchTerm || '').trim().toLowerCase();

            if (!searchTerm) {
                return this.data;
            }

            return this.data.filter(group =>
                String(group?.name || '')
                    .toLowerCase()
                    .includes(searchTerm)
            );
        },
    },

    watch: {
        setValue(newValue) {
            this.selected = newValue;
        },
    },

    async created() {
        this.selected = this.setValue;

        if (this.presetId) {
            await this.setInitialData();
        }
    },

    emits: ['update:modelValue'],

    methods: {
        handleTyping(name) {
            this.latestSearchTerm = name || '';

            if (!name?.length) {
                this.requestId += 1;
                this.restoreDefaultOptions();
                this.reopenSuggestionsPanel();
                return;
            }

            // Clear stale options immediately while the debounced request resolves.
            this.data = [];
            this.getAsyncData(name);
        },

        handleClear() {
            this.latestSearchTerm = '';
            this.selected = null;
            this.restoreDefaultOptions();
            this.reopenSuggestionsPanel();
            this.$emit('update:modelValue', null);
        },

        reopenSuggestionsPanel() {
            this.$nextTick(() => {
                setTimeout(() => {
                    this.$refs.autocomplete?.showSuggestions?.();
                }, 0);
            });
        },

        restoreDefaultOptions() {
            if (this.initialData?.length) {
                this.data = [...this.initialData];
                this.error = null;
                return;
            }

            this.loadInitialData(true);
        },

        async fetchGroups(name = '') {
            const query = name?.length ? `?search=${encodeURIComponent(name)}` : '';
            const { data } = await axios.get(`/api/groups${query}`);
            return data.data;
        },

        getAsyncData: debounce(async function getAsyncData(name) {
            const requestId = ++this.requestId;
            this.isFetching = true;

            try {
                const groups = await this.fetchGroups(name);

                if (requestId !== this.requestId || this.latestSearchTerm !== (name || '')) {
                    return;
                }

                this.data = groups;
                this.error = null;
            } catch (error) {
                if (requestId === this.requestId) {
                    this.data = [];
                    this.error = 'Failed to fetch groups. Please try again.';
                }
                throw error;
            } finally {
                if (requestId === this.requestId) {
                    this.isFetching = false;
                }
            }
        }, 500),

        async loadInitialData(force = false) {
            if (this.latestSearchTerm?.length) {
                return;
            }

            if (!force && this.data.length) {
                return;
            }

            const requestId = ++this.requestId;
            this.isFetching = true;

            try {
                const groups = await this.fetchGroups();

                if (requestId !== this.requestId || this.latestSearchTerm?.length) {
                    return;
                }

                this.data = groups;
                this.error = null;
            } catch (error) {
                if (requestId === this.requestId) {
                    this.data = [];
                    this.error = 'Failed to fetch groups. Please try again.';
                }
                throw error;
            } finally {
                if (requestId === this.requestId) {
                    this.isFetching = false;
                }
            }
        },

        async setInitialData() {
            try {
                const { data } = await axios.get(`/api/groups/${this.presetId}`);

                this.selected = data.data.name;
                this.$emit('update:modelValue', data.data);
                this.error = null;
            } catch (error) {
                this.error = 'Failed to fetch groups. Please try again.';
                console.error(error);
            }
        },
    },
};
</script>

<style>
.error-message {
    color: red;
    margin-top: 0.5rem;
    margin-bottom: -0.5rem;
}
</style>
